// Single global keydown listener mounted once at <App />. Normalises the event to a combo
// string ("alt+g", "ctrl+k", "f8", "escape"), resolves against the active keymap, fires the handler.
//
// HARD RULE (CLAUDE.md): every binding sets preventDefault so browser defaults (F5, Ctrl+P, F11)
// are overridden — required for Tally-keyboard parity. The "Disable browser shortcuts" toggle
// (Phase 1 setting) flips this off if needed.

import { useEffect } from 'react';
import { useShortcutRegistry } from '@/shortcuts/registry';

const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta']);

function eventToCombo(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  if (e.metaKey) parts.push('meta');
  let key = e.key.toLowerCase();
  // Map special keys to short forms
  if (key === ' ') key = 'space';
  if (key === 'arrowup') key = 'up';
  if (key === 'arrowdown') key = 'down';
  if (key === 'arrowleft') key = 'left';
  if (key === 'arrowright') key = 'right';
  parts.push(key);
  return parts.join('+');
}

function isEditableTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  return false;
}

// Allowed inside input/textarea elements — function keys + a small set of nav shortcuts.
// Deliberately EXCLUDES ctrl+a (would steal "select all text"), ctrl+s (bubbles to the
// form's own save handler), and ALL Alt+letter navigation combos — jumping to a report
// while typing in a form would lose entered data. F-key voucher switches are allowed
// (Tally parity) and guarded by a discard confirmation in the App-level handler.
const ALWAYS_ALLOWED_IN_INPUTS = new Set([
  'escape',
  'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10', 'f11', 'f12',
  'ctrl+k',
  'alt+f3', 'alt+f5', 'alt+f6',
]);

export function useGlobalShortcuts(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    function onKey(e: KeyboardEvent) {
      // Don't fire when only a modifier key is pressed
      if (MODIFIER_KEYS.has(e.key)) return;

      const combo = eventToCombo(e);

      // If user is typing in an input, only fire shortcuts that are explicitly allowed there.
      if (isEditableTarget(e) && !ALWAYS_ALLOWED_IN_INPUTS.has(combo)) return;

      const registry = useShortcutRegistry.getState();
      const binding = registry.resolve(combo);
      if (!binding) return;

      const invoked = registry.invoke(binding.id);

      // Only swallow the event if a handler actually fired. Otherwise let the event
      // bubble so component-scoped fallbacks (react-hotkeys-hook inside FormDrawer,
      // browser-default Esc on dialogs, etc.) still work.
      if (invoked && binding.preventDefault !== false) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [enabled]);
}
